import { useMemo } from 'react';

import {
  FetchSelectedMaterialGqlQuery,
  SelectedMaterialUUID
} from '../../selectedMaterialsTypes';

import { FetchSelectedMaterialCacheKey } from '../../selectedMaterialsTypes';

import { useShowQuery } from '../../../common/reactQuery/hooks/useShowQuery';

interface SelectedMaterialResponse<ItemType> {
  selectedMaterial: ItemType | null;
}

interface SelectedMaterialOptions<ItemType> {
  selectedMaterials?: ItemType[];
  cacheKey: FetchSelectedMaterialCacheKey;
  query: FetchSelectedMaterialGqlQuery;
  uuid: SelectedMaterialUUID;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
}

interface UseSelectedMaterialItem {
  uuid: SelectedMaterialUUID;
}

const itemKey = 'selectedMaterial';

function useSelectedMaterial<ItemType extends UseSelectedMaterialItem>({
  selectedMaterials,
  cacheKey,
  query,
  uuid,
  options = {}
}: SelectedMaterialOptions<ItemType>) {
  const placeholderData = useMemo<SelectedMaterialResponse<ItemType>>(() => {
    const selectedMaterial = selectedMaterials?.find(
      (selectedMaterial) => selectedMaterial.uuid === uuid
    );

    return selectedMaterial ? { selectedMaterial } : null;
  }, [selectedMaterials, uuid]);

  const {
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemFetching,
    itemLoading,
    itemIsPlaceholderData,
    refetchItem,
    updateItemCache
  } = useShowQuery<SelectedMaterialResponse<ItemType>, ItemType>({
    cacheKey,
    itemKey,
    options,
    placeholderData,
    query,
    uuid
  });

  return {
    selectedMaterial: item,
    selectedMaterialError: itemError,
    selectedMaterialErrorData: itemErrorData,
    selectedMaterialFetched: itemFetched,
    selectedMaterialFetching: itemFetching,
    selectedMaterialIsPlaceholderData: itemIsPlaceholderData,
    selectedMaterialLoading: itemLoading,
    selectedMaterialUpdateCache: updateItemCache,
    refetchSelectedMaterial: refetchItem
  };
}

export default useSelectedMaterial;
